/* eslint-disable unicorn/no-nested-ternary */
import type { Configuration } from '@azure/msal-browser'
import { LogLevel } from '@azure/msal-browser'
import type { IHopSkipConfig } from './config'
import {
	demoHopSkipConfig,
	developmentHopSkipConfig,
	localHopSkipConfig,
	productionHopSkipConfig,
	qaHopSkipConfig
} from './config'

const ua = globalThis.navigator.userAgent
const isIE = /MSIE|Trident/.test(ua)

export const getHopSkipConfig = (): IHopSkipConfig => {
	const url = globalThis.location.hostname
	const configMap: Record<string, unknown> = {
		localhost: localHopSkipConfig,
		'appdev.myhopskip.com': developmentHopSkipConfig,
		'appdemo.myhopskip.com': demoHopSkipConfig,
		'appqasbx.myhopskip.com': qaHopSkipConfig,
		'app.myhopskip.com': productionHopSkipConfig
	}
	const matchingKey = Object.keys(configMap).find(key => url.includes(key))
	return (matchingKey ? configMap[matchingKey] : undefined) as IHopSkipConfig
}

const logLevelConfig: Record<string, LogLevel> = {
	error: LogLevel.Error,
	warning: LogLevel.Warning,
	info: LogLevel.Info,
	verbose: LogLevel.Verbose
}

export const b2cPolicies = {
	names: {
		signUpSignIn: `B2C_1_HopSkip_Signin_${getHopSkipConfig().auth.policyEnvironment}`,
		signUpOnly: `B2C_1_HopSkip_Signup_${getHopSkipConfig().auth.policyEnvironment}`,
		checkout: `B2C_1_HopSkip_Checkout_${getHopSkipConfig().auth.policyEnvironment}`
	},
	authorities: {
		signUpSignIn: {
			authority: `${getHopSkipConfig().auth.loginDomain}/tfp/${getHopSkipConfig().auth.tenant}/B2C_1_HopSkip_Signin_${getHopSkipConfig().auth.policyEnvironment}`
		},
		signUpOnly: {
			authority: `${getHopSkipConfig().auth.loginDomain}/tfp/${getHopSkipConfig().auth.tenant}/B2C_1_HopSkip_Signup_${getHopSkipConfig().auth.policyEnvironment}`
		},
		checkout: {
			authority: `${getHopSkipConfig().auth.loginDomain}/tfp/${getHopSkipConfig().auth.tenant}/B2C_1_HopSkip_Checkout_${getHopSkipConfig().auth.policyEnvironment}`
		}
	},
	authorityDomain: getHopSkipConfig().auth.loginDomain
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
	auth: {
		clientId: getHopSkipConfig().auth.clientId,
		authority: /checkout/.test(globalThis.location.hash)
			? b2cPolicies.authorities.checkout.authority
			: /signup/.test(globalThis.location.pathname)
				? b2cPolicies.authorities.signUpOnly.authority
				: b2cPolicies.authorities.signUpSignIn.authority,
		knownAuthorities: [b2cPolicies.authorityDomain],
		redirectUri: getHopSkipConfig().clientRoot ?? getHopSkipConfig().api.host,
		postLogoutRedirectUri:
			getHopSkipConfig().clientRoot ?? getHopSkipConfig().api.host,
		navigateToLoginRequestUrl: true
	},
	cache: {
		cacheLocation: 'localStorage',
		storeAuthStateInCookie: isIE
	},
	system: {
		iframeHashTimeout: 10_000,
		loggerOptions: {
			logLevel:
				logLevelConfig[getHopSkipConfig().auth.logLevel] || LogLevel.Error,
			loggerCallback: (
				level: LogLevel,
				message: string,
				containsPii: boolean
			): string => {
				if (containsPii) {
					return ''
				}
				if (
					(logLevelConfig[getHopSkipConfig().auth.logLevel] || LogLevel.Error) <
					level
				) {
					return ''
				}
				switch (level) {
					case LogLevel.Error: {
						console.error(message)
						break
					}
					case LogLevel.Warning: {
						console.warn(message)
						break
					}
					case LogLevel.Info: {
						console.info(message)
						break
					}
					case LogLevel.Verbose: {
						console.debug(message)
						break
					}
					default: {
						console.log(message)
						break
					}
				}
				return ''
			}
		}
	}
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
	scopes: []
}

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
	graphMe: {
		endpoint: 'https://graph.microsoft.com/v1.0/me',
		scopes: ['User.Read']
	},
	apiHopSkip: {
		endpoint: 'https://book.myhopskip.com/api',
		scopes: [
			`${getHopSkipConfig().auth.scopeIdentifierUri}/read`,
			`${getHopSkipConfig().auth.scopeIdentifierUri}/write`,
			`${getHopSkipConfig().auth.scopeIdentifierUri}/delete`
		]
	}
}
